import { generateBillingPayment } from '@/lib/api/financial/financial-billing-api'
import { useState } from 'react'
import { toast } from 'react-toastify'
import {
    MdCheckCircle,
    MdContentCopy,
    MdLink,
    MdOpenInNew,
    MdQrCode,
    MdShare,
    MdWhatsapp,
    MdZoomIn,
} from 'react-icons/md'
import type { BillingInfoData } from './BillingInfoSection'

/**
 * Métodos que o pagador conclui pelo checkout público (`/pay/{token}`). Sem método
 * definido o checkout deixa o pagador escolher, então também vale o link. Dinheiro,
 * maquininha, transferência e "outro" são recebidos fora do sistema: o checkout não
 * tem o que oferecer e o link levaria a uma página sem forma de pagar.
 */
const CHECKOUT_LINK_METHODS = ['PIX', 'CREDIT_CARD', 'DEBIT_CARD', 'BOLETO']

interface BillingPaymentShareProps {
    billing: Pick<
        BillingInfoData,
        | 'id'
        | 'status'
        | 'paymentMethod'
        | 'payerType'
        | 'publicToken'
        | 'pixQrCodeBase64'
        | 'pixCopyPaste'
    >
    /** Pode gerar o QR que faltou (`financial:billings:update`). */
    canGenerate?: boolean
    onChanged?: () => void | Promise<void>
}

/**
 * Como o cliente paga uma cobrança em aberto: o QR Code do PIX, em tamanho para ser
 * lido pela câmera do celular do cliente, o copia-e-cola e o link de pagamento com
 * as ações de enviar. Fica no card de cobrança das telas de atendimento e de pedido,
 * onde o operador está com o cliente na frente (balcão, recepção).
 */
export default function BillingPaymentShare({
    billing,
    canGenerate = false,
    onChanged,
}: BillingPaymentShareProps) {
    const [copiedField, setCopiedField] = useState<string | null>(null)
    const [showLargeQr, setShowLargeQr] = useState(false)
    const [generating, setGenerating] = useState(false)

    const isPending =
        billing.status === 'PENDING' || billing.status === 'OVERDUE'
    // Convênio não passa pelo gateway: quem paga é a fatura do período.
    if (!isPending || billing.payerType === 'HEALTH_PLAN') return null

    const isPix = billing.paymentMethod === 'PIX'
    const hasPixData = !!billing.pixQrCodeBase64 || !!billing.pixCopyPaste
    const checkoutUrl =
        billing.publicToken &&
        (!billing.paymentMethod ||
            CHECKOUT_LINK_METHODS.includes(billing.paymentMethod))
            ? `${window.location.origin}/pay/${billing.publicToken}`
            : null

    if (!isPix && !checkoutUrl) return null

    const shareText = `Link para pagamento: ${checkoutUrl}`
    const canNativeShare =
        typeof navigator !== 'undefined' &&
        typeof navigator.share === 'function'

    const handleCopy = async (text: string, field: string) => {
        try {
            await navigator.clipboard.writeText(text)
            setCopiedField(field)
            toast.success('Copiado!')
            setTimeout(() => setCopiedField(null), 2000)
        } catch {
            toast.error('Erro ao copiar')
        }
    }

    const handleNativeShare = async () => {
        if (!checkoutUrl) return
        try {
            await navigator.share({
                title: 'Pagamento',
                text: shareText,
                url: checkoutUrl,
            })
        } catch {
            // Fechar a folha de compartilhamento também rejeita: não é erro.
        }
    }

    const handleGenerate = async () => {
        if (generating) return
        setGenerating(true)
        try {
            const response = await generateBillingPayment(billing.id)
            if (response.success) {
                toast.success('QR Code PIX gerado')
                await onChanged?.()
            } else {
                toast.error(
                    response.error || 'Não foi possível gerar o QR Code PIX'
                )
            }
        } catch {
            toast.error('Não foi possível gerar o QR Code PIX')
        } finally {
            setGenerating(false)
        }
    }

    const copyIcon = (field: string) =>
        copiedField === field ? (
            <MdCheckCircle className="text-success text-base" />
        ) : (
            <MdContentCopy className="text-base" />
        )

    return (
        <div className="mb-4 grid gap-4 md:grid-cols-2">
            {isPix && (
                <div className="rounded-xl border border-base-300/60 bg-base-100 p-4">
                    <div className="mb-3 flex items-center gap-2">
                        <MdQrCode className="text-lg text-secondary" />
                        <h4 className="font-semibold text-base-content">PIX</h4>
                    </div>

                    {hasPixData ? (
                        <div className="flex flex-col items-center gap-3">
                            {billing.pixQrCodeBase64 && (
                                <>
                                    {/* Fundo branco fixo: no tema escuro o QR perde o
                                        contraste e a câmera não lê. */}
                                    <button
                                        type="button"
                                        onClick={() => setShowLargeQr(true)}
                                        className="rounded-xl bg-white p-3 shadow-sm"
                                        title="Ampliar QR Code"
                                    >
                                        <img
                                            src={`data:image/png;base64,${billing.pixQrCodeBase64}`}
                                            alt="QR Code PIX"
                                            className="h-60 w-60 sm:h-64 sm:w-64"
                                        />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowLargeQr(true)}
                                        className="btn btn-ghost btn-xs gap-1"
                                    >
                                        <MdZoomIn className="text-base" />
                                        Ampliar
                                    </button>
                                </>
                            )}

                            {billing.pixCopyPaste && (
                                <div className="w-full">
                                    <p className="mb-1 text-xs text-base-content/60">
                                        PIX copia e cola
                                    </p>
                                    <div className="flex items-stretch gap-2">
                                        <p className="flex-1 truncate rounded-lg bg-base-200 px-3 py-2 font-mono text-xs">
                                            {billing.pixCopyPaste}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleCopy(
                                                    billing.pixCopyPaste!,
                                                    'pix'
                                                )
                                            }
                                            className="btn btn-sm btn-outline btn-secondary gap-1"
                                        >
                                            {copyIcon('pix')}
                                            Copiar
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col items-start gap-3">
                            <p className="text-sm text-base-content/70">
                                O QR Code PIX ainda não foi gerado para esta
                                cobrança.
                            </p>
                            {canGenerate && (
                                <button
                                    type="button"
                                    onClick={handleGenerate}
                                    disabled={generating}
                                    className="btn btn-sm btn-secondary gap-1"
                                >
                                    {generating ? (
                                        <span className="loading loading-spinner loading-xs" />
                                    ) : (
                                        <MdQrCode className="text-base" />
                                    )}
                                    Gerar QR Code
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {checkoutUrl && (
                <div className="rounded-xl border border-base-300/60 bg-base-100 p-4">
                    <div className="mb-3 flex items-center gap-2">
                        <MdLink className="text-lg text-secondary" />
                        <h4 className="font-semibold text-base-content">
                            Link de pagamento
                        </h4>
                    </div>
                    <p className="mb-3 text-xs text-base-content/60">
                        Envie ao cliente para pagar pelo celular ou computador.
                    </p>
                    <p className="mb-3 truncate rounded-lg bg-base-200 px-3 py-2 font-mono text-xs">
                        {checkoutUrl}
                    </p>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => handleCopy(checkoutUrl, 'link')}
                            className="btn btn-sm btn-outline btn-secondary gap-1"
                        >
                            {copyIcon('link')}
                            Copiar
                        </button>
                        <a
                            href={checkoutUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-ghost gap-1"
                        >
                            <MdOpenInNew className="text-base" />
                            Abrir
                        </a>
                        <a
                            href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-ghost gap-1"
                        >
                            <MdWhatsapp className="text-base" />
                            WhatsApp
                        </a>
                        {canNativeShare && (
                            <button
                                type="button"
                                onClick={handleNativeShare}
                                className="btn btn-sm btn-ghost gap-1"
                            >
                                <MdShare className="text-base" />
                                Compartilhar
                            </button>
                        )}
                    </div>
                </div>
            )}

            {showLargeQr && billing.pixQrCodeBase64 && (
                <div
                    className="modal modal-open"
                    onClick={() => setShowLargeQr(false)}
                >
                    <div
                        className="modal-box flex max-w-lg flex-col items-center gap-4"
                        onClick={e => e.stopPropagation()}
                    >
                        <h3 className="text-lg font-bold">QR Code PIX</h3>
                        <div className="rounded-xl bg-white p-4">
                            <img
                                src={`data:image/png;base64,${billing.pixQrCodeBase64}`}
                                alt="QR Code PIX"
                                className="h-[min(80vw,24rem)] w-[min(80vw,24rem)]"
                            />
                        </div>
                        <p className="text-center text-sm text-base-content/60">
                            Vire a tela para o cliente escanear com o celular.
                        </p>
                        <div className="modal-action mt-0">
                            <button
                                type="button"
                                className="btn"
                                onClick={() => setShowLargeQr(false)}
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
